// Guía para Principiantes - Explicaciones ultra simples
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './BeginnerGuide.css';

const BeginnerGuide = () => {
    const [expandedTool, setExpandedTool] = useState(null);

    const tools = [
        {
            id: 'github',
            name: 'GitHub',
            emoji: '📦',
            tagline: 'Tu almacén en la nube',
            simpleExplanation: 'Imagina que estás construyendo una casa de LEGO. GitHub es como una caja mágica donde guardas todas tus piezas de LEGO en internet. Si se rompe tu ordenador, no pasa nada, tus piezas siguen ahí guardadas.',
            realWorldExample: 'Es como Google Drive, pero especial para guardar código de programas.',
            whyYouNeedIt: 'Para no perder nunca tu trabajo. Y para que otros puedan ver tu proyecto si quieres.',
            cost: 'GRATIS - ilimitado',
            difficulty: 'Fácil',
            timeToSetup: '5 minutos',
            steps: [
                'Ve a github.com',
                'Haz click en "Sign up"',
                'Pon tu email y crea una contraseña',
                '¡Listo! Ya tienes tu almacén'
            ]
        },
        {
            id: 'supabase',
            name: 'Supabase',
            emoji: '🗄️',
            tagline: 'Tu base de datos (donde se guardan los datos)',
            simpleExplanation: 'Imagina una hoja de Excel gigante que vive en internet. Ahí puedes guardar información: nombres de usuarios, sus fotos, sus mensajes... todo organizado en tablas.',
            realWorldExample: 'Cuando te registras en Instagram, tu nombre y foto se guardan en una base de datos como Supabase.',
            whyYouNeedIt: 'Para que tu app recuerde cosas. Si no tienes base de datos, cada vez que cierras la app se olvida todo.',
            cost: 'GRATIS hasta 500MB (suficiente para empezar)',
            difficulty: 'Media',
            timeToSetup: '10 minutos',
            steps: [
                'Ve a supabase.com',
                'Crea cuenta con tu GitHub (un click)',
                'Click en "New Project"',
                'Ponle un nombre y contraseña',
                'Espera 2 minutos... ¡listo!'
            ]
        },
        {
            id: 'cursor',
            name: 'Cursor',
            emoji: '✨',
            tagline: 'Tu editor de código con IA que te ayuda',
            simpleExplanation: 'Es como Word, pero para escribir código. Lo especial es que tiene un asistente de IA integrado. Tú le dices "hazme un botón rojo" y él te escribe el código.',
            realWorldExample: 'Es como tener un programador experto sentado a tu lado 24/7 que te ayuda con todo.',
            whyYouNeedIt: 'Para escribir código sin saber programar. La IA hace el trabajo pesado por ti.',
            cost: 'GRATIS (limitado) o €20/mes (ilimitado)',
            difficulty: 'Fácil',
            timeToSetup: '10 minutos',
            steps: [
                'Ve a cursor.com',
                'Descarga la app para tu ordenador',
                'Instálala como cualquier programa',
                'Abre Cursor y conecta tu cuenta',
                'Empieza a pedirle cosas a la IA'
            ]
        },
        {
            id: 'antigravity',
            name: 'Antigravity',
            emoji: '🚀',
            tagline: 'IA que construye apps completas',
            simpleExplanation: 'Imagina que le dices a alguien "quiero una app para pedir pizzas" y en 5 minutos te la construye entera. Eso es Antigravity.',
            realWorldExample: 'Es como contratar a un equipo de programadores, pero es una IA que trabaja en segundos.',
            whyYouNeedIt: 'Para crear proyectos grandes rápidamente. Ideal cuando ya tienes claro qué quieres construir.',
            cost: 'Tiene planes gratuitos y de pago',
            difficulty: 'Fácil',
            timeToSetup: '5 minutos',
            steps: [
                'Ve a antigravity.dev',
                'Crea una cuenta',
                'Describe tu idea en español',
                'La IA genera tu proyecto',
                'Descárgalo o súbelo a GitHub'
            ]
        },
        {
            id: 'vercel',
            name: 'Vercel',
            emoji: '🌐',
            tagline: 'Publica tu web en internet',
            simpleExplanation: 'Tu proyecto vive en tu ordenador. Vercel lo pone en internet para que cualquier persona del mundo pueda verlo.',
            realWorldExample: 'Es como subir un video a YouTube. Tu video está en tu ordenador, pero cuando lo subes, todo el mundo puede verlo.',
            whyYouNeedIt: 'Para que tu app no sea solo tuya. Para compartirla con amigos, clientes, o el mundo entero.',
            cost: 'GRATIS para proyectos personales',
            difficulty: 'Fácil',
            timeToSetup: '5 minutos',
            steps: [
                'Ve a vercel.com',
                'Conecta tu cuenta de GitHub',
                'Selecciona tu proyecto',
                'Click en "Deploy"',
                '¡Tu web ya está online!'
            ]
        },
        {
            id: 'roadmap',
            name: 'Roadmap Platform',
            emoji: '🗺️',
            tagline: 'Tu guía paso a paso (esta web)',
            simpleExplanation: 'Es como un GPS para construir tu proyecto. Te dice exactamente qué hacer primero, qué hacer después, y te avisa cuando terminas cada paso.',
            realWorldExample: 'Como cuando sigues una receta de cocina paso a paso. No te pierdes porque te dice exactamente qué hacer.',
            whyYouNeedIt: 'Para no perderte. Sin un plan, empiezas muchas cosas y no terminas ninguna. Con Roadmap, cada paso está claro.',
            cost: '€2.99/mes después del periodo de prueba',
            difficulty: 'Muy fácil',
            timeToSetup: '2 minutos',
            steps: [
                'Crea tu cuenta (gratis)',
                'Describe tu idea de proyecto',
                'La IA genera tu roadmap',
                'Sigue los pasos uno a uno',
                'Marca como completado cada tarea'
            ]
        }
    ];

    const processSteps = [
        {
            number: 1,
            title: 'Tienes una idea',
            description: 'Quieres crear algo: una app, una web, un juego... lo que sea.',
            example: '"Quiero crear una app para que mi tienda reciba pedidos online"',
            icon: '💡'
        },
        {
            number: 2,
            title: 'Creas tu cuenta gratis',
            description: 'Te registras con GitHub (tu almacén de código) en nuestra plataforma.',
            example: 'Click en "Continuar con GitHub" y listo.',
            icon: '👤'
        },
        {
            number: 3,
            title: 'Describes tu proyecto',
            description: 'Nos cuentas qué quieres crear. En español, con tus palabras.',
            example: '"Una web donde mis clientes puedan ver el menú y hacer pedidos"',
            icon: '💬'
        },
        {
            number: 4,
            title: 'Generamos tu roadmap',
            description: 'Nuestra IA crea un plan detallado con todas las tareas que necesitas hacer.',
            example: 'Fase 1: Diseño, Fase 2: Base de datos, Fase 3: Funcionalidades...',
            icon: '📋'
        },
        {
            number: 5,
            title: 'Conectas las herramientas',
            description: 'Te guiamos para configurar GitHub, Supabase, y Cursor. Todo explicado.',
            example: 'Tutoriales paso a paso con capturas de pantalla.',
            icon: '🔧'
        },
        {
            number: 6,
            title: 'Construyes con IA',
            description: 'Abres Cursor, le pides que haga las tareas del roadmap, y él programa por ti.',
            example: 'Tú: "Crea el formulario de pedidos". Cursor: *genera el código*',
            icon: '🤖'
        },
        {
            number: 7,
            title: 'Publicas tu proyecto',
            description: 'Cuando terminas, lo subes a Vercel y ya está en internet.',
            example: 'Tu app: mitienda.vercel.app - ¡cualquiera puede usarla!',
            icon: '🌍'
        }
    ];

    return (
        <div className="guide-container">
            <nav className="guide-nav">
                <Link to="/" className="guide-back">
                    ← Volver al inicio
                </Link>
            </nav>

            {/* Header */}
            <header className="guide-header">
                <div className="guide-badge">📚 Guía para Principiantes</div>
                <h1>Todo lo que necesitas saber<br /><span>explicado para cualquiera</span></h1>
                <p>Sin tecnicismos. Sin complicaciones. Como si te lo explicara un amigo.</p>
            </header>

            {/* Quick Summary */}
            <section className="guide-summary">
                <h2>Resumen en 30 segundos</h2>
                <div className="summary-card">
                    <p>
                        <strong>¿Qué vas a hacer?</strong> Crear un proyecto de software real 
                        (app, web, lo que quieras) sin saber programar.
                    </p>
                    <p>
                        <strong>¿Cómo?</strong> Usando herramientas con IA que programan por ti. 
                        Tú solo describes qué quieres.
                    </p>
                    <p>
                        <strong>¿Cuánto cuesta?</strong> Empezar es gratis. Nuestra plataforma 
                        cuesta €2.99/mes (menos que un café). Las herramientas externas tienen 
                        planes gratuitos suficientes para empezar.
                    </p>
                </div>
            </section>

            {/* The Process */}
            <section className="guide-process">
                <h2>El proceso completo (paso a paso)</h2>
                <div className="process-timeline">
                    {processSteps.map((step) => (
                        <div key={step.number} className="process-step">
                            <div className="step-marker">
                                <span className="step-icon">{step.icon}</span>
                                <span className="step-num">{step.number}</span>
                            </div>
                            <div className="step-content">
                                <h3>{step.title}</h3>
                                <p>{step.description}</p>
                                <div className="step-example">
                                    <span className="example-label">Ejemplo:</span>
                                    <span>{step.example}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Tools Explained */}
            <section className="guide-tools">
                <h2>Las herramientas explicadas</h2>
                <p className="tools-intro">
                    Click en cada una para ver la explicación completa
                </p>
                
                <div className="tools-list">
                    {tools.map((tool) => (
                        <div 
                            key={tool.id}
                            className={`tool-item ${expandedTool === tool.id ? 'expanded' : ''}`}
                        >
                            <button 
                                className="tool-header"
                                onClick={() => setExpandedTool(
                                    expandedTool === tool.id ? null : tool.id
                                )}
                            >
                                <div className="tool-main">
                                    <span className="tool-emoji">{tool.emoji}</span>
                                    <div className="tool-info">
                                        <h3>{tool.name}</h3>
                                        <p>{tool.tagline}</p>
                                    </div>
                                </div>
                                <div className="tool-meta">
                                    <span className={`tool-cost ${tool.cost.includes('GRATIS') ? 'free' : 'paid'}`}>
                                        {tool.cost.includes('GRATIS') ? '✓ Gratis' : tool.cost}
                                    </span>
                                    <span className="tool-expand">
                                        {expandedTool === tool.id ? '−' : '+'}
                                    </span>
                                </div>
                            </button>
                            
                            {expandedTool === tool.id && (
                                <div className="tool-details">
                                    <div className="detail-section">
                                        <h4>🎯 Explicación simple</h4>
                                        <p>{tool.simpleExplanation}</p>
                                    </div>
                                    
                                    <div className="detail-section">
                                        <h4>🌍 Ejemplo del mundo real</h4>
                                        <p>{tool.realWorldExample}</p>
                                    </div>
                                    
                                    <div className="detail-section">
                                        <h4>❓ ¿Por qué lo necesitas?</h4>
                                        <p>{tool.whyYouNeedIt}</p>
                                    </div>

                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <span className="label">💰 Precio</span>
                                            <span className="value">{tool.cost}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">📊 Dificultad</span>
                                            <span className="value">{tool.difficulty}</span>
                                        </div>
                                        <div className="detail-item">
                                            <span className="label">⏱️ Tiempo setup</span>
                                            <span className="value">{tool.timeToSetup}</span>
                                        </div>
                                    </div>

                                    <div className="detail-section">
                                        <h4>📝 Cómo configurarlo</h4>
                                        <ol className="setup-steps">
                                            {tool.steps.map((step, idx) => (
                                                <li key={idx}>{step}</li>
                                            ))}
                                        </ol>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* FAQ */}
            <section className="guide-faq">
                <h2>Preguntas frecuentes</h2>
                
                <div className="faq-grid">
                    <div className="faq-item">
                        <h4>¿Necesito saber programar?</h4>
                        <p>No. La IA programa por ti. Tú solo describes qué quieres en español.</p>
                    </div>
                    
                    <div className="faq-item">
                        <h4>¿Cuánto tiempo tarda?</h4>
                        <p>Depende del proyecto. Una web simple: días. Una app compleja: semanas. Pero siempre más rápido que aprender a programar desde cero.</p>
                    </div>
                    
                    <div className="faq-item">
                        <h4>¿El proyecto es mío?</h4>
                        <p>100% tuyo. El código se guarda en TU GitHub. Puedes hacer lo que quieras con él.</p>
                    </div>
                    
                    <div className="faq-item">
                        <h4>¿Puedo ganar dinero con lo que creo?</h4>
                        <p>Sí. Muchos usuarios crean proyectos para vender o para sus negocios.</p>
                    </div>
                    
                    <div className="faq-item">
                        <h4>¿Qué pasa si me atasco?</h4>
                        <p>El roadmap te guía paso a paso. Si aún así tienes dudas, la IA de Cursor puede ayudarte.</p>
                    </div>
                    
                    <div className="faq-item">
                        <h4>¿Por qué €2.99/mes?</h4>
                        <p>Para mantener la plataforma, mejorarla, y seguir creando contenido que te ayude. Es menos que un café.</p>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="guide-cta">
                <h2>¿Listo para empezar?</h2>
                <p>Crea tu cuenta gratis y explora la plataforma. Solo pagas cuando quieras usar todas las funciones.</p>
                <div className="cta-buttons">
                    <Link to="/register" className="btn-primary">
                        Crear cuenta gratis →
                    </Link>
                    <Link to="/demo" className="btn-secondary">
                        Ver demo primero
                    </Link>
                </div>
                <p className="cta-note">
                    Después del periodo de prueba: €2.99/mes · Cancela cuando quieras
                </p>
            </section>

            <footer className="guide-footer">
                <p>Parte del ecosistema FIXARR · <Link to="/privacy-policy">Privacidad</Link></p>
            </footer>
        </div>
    );
};

export default BeginnerGuide;
